import React, { useEffect, useState } from 'react';
import { ArrowRight, BadgeCheck, Clock3, ShieldAlert, UsersRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../../api/dashboardApi';
import { AdminLayout } from '../../components/layouts';
import { Badge, Card, CardBody, CardHeader, EmptyState, StatCard } from '../../components/ui';

const STATUS_VARIANT = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await dashboardApi.admin();
        setData(response.data?.data);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <Card>
          <EmptyState title="Loading dashboard…" description="Fetching the latest marketplace activity." />
        </Card>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout title="Dashboard">
        <Card>
          <EmptyState title="Dashboard unavailable" description={error || 'Failed to load dashboard'} />
        </Card>
      </AdminLayout>
    );
  }

  const stats = data.stats || {};
  const cards = [
    { label: 'Total Giggers', value: stats.totalProviders || 0, icon: <UsersRound size={21} />, tone: 'green' },
    { label: 'Pending Review', value: stats.pendingVerifications || 0, icon: <Clock3 size={21} />, tone: 'amber' },
    { label: 'Approved', value: stats.approvedProviders || 0, icon: <BadgeCheck size={21} />, tone: 'blue' },
    { label: 'Rejected', value: stats.rejectedProviders || 0, icon: <ShieldAlert size={21} />, tone: 'red' },
  ];

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <AdminLayout title="Dashboard">
      <div className="mb-6">
        <h2 className="m-0 font-nunito text-[24px] font-black tracking-[-0.025em] text-giggs-ink sm:text-[28px]">Marketplace overview</h2>
        <p className="mb-0 mt-1 text-sm leading-6 text-giggs-muted">Review provider activity, verification workload and recent registrations at a glance.</p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
        {cards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
        <Card className="min-w-0 self-start">
          <CardHeader
            title="Pending actions"
            description="Items that currently need admin attention."
            action={(
              <Link to="/admin/verifications" className="inline-flex items-center gap-1.5 text-xs font-bold text-giggs-green no-underline transition-all duration-200 hover:gap-2.5 hover:text-giggs-green-hover">
                Open queue <ArrowRight size={14} />
              </Link>
            )}
          />
          <CardBody className="pt-1">
            {data.pendingActions?.length > 0 ? (
              <div className="flex flex-col gap-2">
                {data.pendingActions.map((action, index) => (
                  <Link
                    key={`${action.label}-${index}`}
                    to="/admin/verifications"
                    className="group flex min-w-0 items-center justify-between gap-4 rounded-xl bg-[#F8FAF9] px-4 py-3.5 text-sm text-giggs-text no-underline transition-all duration-200 hover:-translate-y-0.5 hover:bg-amber-50 hover:text-giggs-ink hover:shadow-sm"
                  >
                    <span className="min-w-0 break-words font-semibold">{action.label}</span>
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-giggs-green shadow-sm transition-transform duration-200 group-hover:translate-x-0.5">
                      <ArrowRight size={15} />
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState title="All caught up" description="There are no pending admin actions right now." className="py-10" />
            )}
          </CardBody>
        </Card>

        <Card className="min-w-0">
          <CardHeader
            title="Recent Giggers"
            description="Latest provider registrations across the platform."
            action={(
              <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-xs font-bold text-giggs-green no-underline transition-all duration-200 hover:gap-2.5 hover:text-giggs-green-hover">
                View all <ArrowRight size={14} />
              </Link>
            )}
          />

          <CardBody className="pt-1">
            {data.recentProviders?.length ? (
              <div className="flex flex-col gap-1.5">
                {data.recentProviders.map((provider) => {
                  const status = provider.verificationStatus || 'pending';
                  return (
                    <div
                      key={provider._id}
                      className="group grid min-w-0 grid-cols-[42px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-xl px-2 py-2.5 transition-all duration-200 hover:bg-[#F8FAF9] sm:grid-cols-[42px_minmax(0,1fr)_auto_auto] sm:px-3"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#DCFCE7,#BBF7D0)] font-nunito font-black text-[#177552] ring-1 ring-inset ring-emerald-100 transition-transform duration-200 group-hover:scale-105">
                        {(provider.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-giggs-ink">{provider.name}</div>
                        <div className="truncate text-xs text-giggs-muted">{provider.email}</div>
                      </div>
                      <div className="col-start-2 sm:col-start-auto">
                        <Badge variant={STATUS_VARIANT[status] || 'neutral'} className="capitalize">{status}</Badge>
                      </div>
                      <span className="col-start-2 text-xs font-medium text-giggs-muted sm:col-start-auto sm:min-w-[90px] sm:text-right">{fmtDate(provider.createdAt)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No Giggers yet" description="New provider registrations will appear here." className="py-10" />
            )}
          </CardBody>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
